import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useQuery, useQueryClient } from 'react-query'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { UserCircle, Users, Plus, Bot, ListFilter } from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import { projectService } from '@/services/projectService'
import ProjectForm from '@/components/ProjectCentral/ProjectForm'
import { useAuthStore } from '@/store/authStore'

const SortableProjectItem = ({ project, isActive, collapsed, onClick }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: project.id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onClick}
      className={cn(
        'w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-sm text-left transition-colors cursor-pointer active:cursor-grabbing',
        isActive
          ? 'bg-gray-100 text-sidebar-foreground font-medium dark:bg-sidebar-accent dark:text-sidebar-accent-foreground'
          : 'text-sidebar-foreground/70 hover:bg-gray-100 hover:text-sidebar-foreground dark:hover:bg-sidebar-accent dark:hover:text-sidebar-accent-foreground',
        isDragging && 'opacity-50 z-50',
        collapsed && 'justify-center px-2'
      )}
      title={collapsed ? project.name : undefined}
    >
      <span className="w-4 h-4 shrink-0 flex items-center justify-center">
        <span
          className="w-2.5 h-2.5 rounded-sm"
          style={{ backgroundColor: project.color || '#3B82F6' }}
        />
      </span>
      {!collapsed && (
        <>
          <span className="truncate flex-1 text-left">{project.name}</span>
          {project.taskCount > 0 && (
            <span className="text-[10px] text-muted-foreground bg-muted rounded-full px-1.5 ml-auto shrink-0">
              {project.taskCount}
            </span>
          )}
        </>
      )}
    </div>
  )
}

const ProjectCentralSidebar = ({ collapsed }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const [showCreateProject, setShowCreateProject] = useState(false)
  const { user } = useAuthStore()
  const canCreateProject = user?.role === 'admin'
  const canReorder = user?.role === 'admin'

  const { data: projectsData, isLoading } = useQuery(
    ['projects', { status: 'active' }],
    () => projectService.getProjects({ status: 'active' }),
    { staleTime: 30000 }
  )

  const [sortedProjects, setSortedProjects] = useState([])

  useEffect(() => {
    const projects = projectsData?.data?.projects || []
    setSortedProjects(projects)
  }, [projectsData])

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  )

  const handleDragEnd = async ({ active, over }) => {
    if (!over || active.id === over.id) return

    const oldIndex = sortedProjects.findIndex(p => p.id === active.id)
    const newIndex = sortedProjects.findIndex(p => p.id === over.id)
    if (oldIndex === -1 || newIndex === -1) return

    const reordered = arrayMove(sortedProjects, oldIndex, newIndex)
    setSortedProjects(reordered)

    const updates = reordered.map((p, i) => ({ id: p.id, display_order: i }))

    try {
      await projectService.reorderProjects(updates)
      queryClient.invalidateQueries(['projects'])
    } catch {
      // Revert on failure
      setSortedProjects(sortedProjects)
      toast.error('Failed to reorder projects')
    }
  }

  const isForYouActive = location.pathname === '/projects/for-you'
  const isTeamViewActive = location.pathname === '/projects/team-view'
  const isSearchActive = location.pathname === '/projects/issues'
  const isAdmin = user?.role === 'admin'

  const projectList = canReorder && !collapsed ? (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={sortedProjects.map(p => p.id)}
        strategy={verticalListSortingStrategy}
      >
        <nav className="space-y-0.5">
          {sortedProjects.map((project) => {
            const isActive = location.pathname.startsWith(`/projects/${project.id}`)
            return (
              <SortableProjectItem
                key={project.id}
                project={project}
                isActive={isActive}
                collapsed={collapsed}
                onClick={() => navigate(`/projects/${project.id}/board`)}
              />
            )
          })}
        </nav>
      </SortableContext>
    </DndContext>
  ) : (
    <nav className="space-y-0.5">
      {sortedProjects.map((project) => {
        const isActive = location.pathname.startsWith(`/projects/${project.id}`)
        return (
          <button
            key={project.id}
            onClick={() => navigate(`/projects/${project.id}/board`)}
            className={cn(
              'w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-sm text-left transition-colors',
              isActive
                ? 'bg-gray-100 text-sidebar-foreground font-medium dark:bg-sidebar-accent dark:text-sidebar-accent-foreground'
                : 'text-sidebar-foreground/70 hover:bg-gray-100 hover:text-sidebar-foreground dark:hover:bg-sidebar-accent dark:hover:text-sidebar-accent-foreground',
              collapsed && 'justify-center px-2'
            )}
            title={collapsed ? project.name : undefined}
          >
            <span className="w-4 h-4 shrink-0 flex items-center justify-center">
              <span
                className="w-2.5 h-2.5 rounded-sm"
                style={{ backgroundColor: project.color || '#3B82F6' }}
              />
            </span>
            {!collapsed && (
              <>
                <span className="truncate flex-1 text-left">{project.name}</span>
                {project.taskCount > 0 && (
                  <span className="text-[10px] text-muted-foreground bg-muted rounded-full px-1.5 ml-auto shrink-0">
                    {project.taskCount}
                  </span>
                )}
              </>
            )}
          </button>
        )
      })}
    </nav>
  )

  return (
    <div>
      {/* For You */}
      <button
        onClick={() => navigate('/projects/for-you')}
        className={cn(
          'w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors',
          isForYouActive
            ? 'bg-gray-100 text-sidebar-foreground font-medium dark:bg-sidebar-accent dark:text-sidebar-accent-foreground'
            : 'text-sidebar-foreground/70 hover:bg-gray-100 hover:text-sidebar-foreground dark:hover:bg-sidebar-accent dark:hover:text-sidebar-accent-foreground',
          collapsed && 'justify-center px-2'
        )}
      >
        <UserCircle className="h-4 w-4 shrink-0" />
        {!collapsed && <span>For you</span>}
      </button>

      {/* Issue search (JQL + saved filters) */}
      <button
        onClick={() => navigate('/projects/issues')}
        className={cn(
          'w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors mt-0.5',
          isSearchActive
            ? 'bg-gray-100 text-sidebar-foreground font-medium dark:bg-sidebar-accent dark:text-sidebar-accent-foreground'
            : 'text-sidebar-foreground/70 hover:bg-gray-100 hover:text-sidebar-foreground dark:hover:bg-sidebar-accent dark:hover:text-sidebar-accent-foreground',
          collapsed && 'justify-center px-2'
        )}
        title={collapsed ? 'Search issues' : undefined}
      >
        <ListFilter className="h-4 w-4 shrink-0" />
        {!collapsed && <span>Search issues</span>}
      </button>

      {/* Team View (admin only) */}
      {isAdmin && (
        <button
          onClick={() => navigate('/projects/team-view')}
          className={cn(
            'w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors mt-0.5',
            isTeamViewActive
              ? 'bg-gray-100 text-sidebar-foreground font-medium dark:bg-sidebar-accent dark:text-sidebar-accent-foreground'
              : 'text-sidebar-foreground/70 hover:bg-gray-100 hover:text-sidebar-foreground dark:hover:bg-sidebar-accent dark:hover:text-sidebar-accent-foreground',
            collapsed && 'justify-center px-2'
          )}
        >
          <Users className="h-4 w-4 shrink-0" />
          {!collapsed && <span>Team view</span>}
        </button>
      )}

      {/* Connect AI tools (Claude Code, Cursor, ...) via API tokens + MCP */}
      <button
        onClick={() => navigate('/profile/api-tokens')}
        className={cn(
          'w-full flex items-center gap-2 px-3 py-1.5 rounded-md text-sm transition-colors mt-0.5',
          'text-sidebar-foreground/70 hover:bg-gray-100 hover:text-sidebar-foreground dark:hover:bg-sidebar-accent dark:hover:text-sidebar-accent-foreground',
          collapsed && 'justify-center px-2'
        )}
        title="Connect AI tools"
      >
        <Bot className="h-4 w-4 shrink-0" />
        {!collapsed && <span>Connect AI tools</span>}
      </button>

      {/* Projects label */}
      {!collapsed && (
        <div className="flex items-center justify-between pl-3 pr-3 pt-6 mb-1">
          <span className="text-[11px] font-medium text-sidebar-foreground/50 uppercase tracking-wider">
            Projects
          </span>
          {canCreateProject && (
            <button
              onClick={() => setShowCreateProject(true)}
              className="text-sidebar-foreground/50 hover:text-sidebar-foreground transition-colors"
              title="Create project"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}

      {/* Project list */}
      {isLoading ? (
        <div className="space-y-1 px-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className={cn('h-7 rounded-md', collapsed ? 'w-7 mx-auto' : 'w-full')} />
          ))}
        </div>
      ) : (
        projectList
      )}
      <ProjectForm
        open={showCreateProject}
        onOpenChange={setShowCreateProject}
      />
    </div>
  )
}

export default ProjectCentralSidebar
